import {useFetch} from "./fetch";
import {UseFetchReturn, resolveUnref, MaybeComputedRef} from "@vueuse/core";
import {
    NewProject,
    NewProjectType,
    Project,
    ProjectInfo,
    ProjectPermission, ProjectPhotoCaption, ProjectProperty, ProjectTemplate,
    ProjectType, ProjectTypeSummary, ProjectUser,
    UpdateProject, UpdateProjectPermission, UpdateProjectType
} from "../models/project";
import {Pagination, Page} from "../models/common";
import {computed} from "vue";
import {Label, NewLabel} from "../models/task";

export function useProjectTypes(): UseFetchReturn<ProjectType[]> {
    return useFetch<ProjectType[]>('/projectTypes', {initialData: []})
        .get()
        .json()
}

export function useProjectTypesList(): UseFetchReturn<ProjectTypeSummary[]> {
    return useFetch<ProjectTypeSummary[]>('/projectTypes/list', {initialData: []})
        .get()
        .json()
}


export function useParentProjectTypes(): UseFetchReturn<ProjectType[]> {
    return useFetch<ProjectType[]>('/projectTypes/parent', {initialData: []})
        .get()
        .json()
}


export function useOwnedProjectTypes(): UseFetchReturn<ProjectType[]> {
    return useFetch<ProjectType[]>('/projectTypes/owned', {initialData: []})
        .get()
        .json()
}

export function useProjectProjectTypes(id: MaybeComputedRef<number>): UseFetchReturn<ProjectType[]> {
    return useFetch<ProjectType[]>(`/projectTypes/project/${resolveUnref(id)}`, {initialData: []})
        .get()
        .json()
}

export function useOrganizationProjectTypes(id: MaybeComputedRef<number>): UseFetchReturn<ProjectType[]> {
    return useFetch<ProjectType[]>(`/projectTypes/organization/${resolveUnref(id)}`, {initialData: []})
        .get()
        .json()
}

export function useAddProjectTypes(request: NewProjectType): UseFetchReturn<null> {
    return useFetch<null>('/projectTypes')
        .post(request)
}


export function useUpdateProjectTypes(id: MaybeComputedRef<number>, request: UpdateProjectType): UseFetchReturn<null> {
    return useFetch<null>(`/projectTypes/${resolveUnref(id)}`)
        .put(request)
}


export function useDeleteProjectTypes(id: MaybeComputedRef<number>): UseFetchReturn<null> {
    return useFetch<null>(`/projectTypes/${resolveUnref(id)}`)
        .delete()
}

export function useProjects(type: number | undefined): (pagination: Pagination) => UseFetchReturn<Page<Project>> {
    return useBaseProjects(`projects`, type)
}

export function useOrganizationProjects(organizationId: MaybeComputedRef<number>, type: number | undefined): (pagination: Pagination) => UseFetchReturn<Page<Project>> {
    return useBaseProjects(`organizations/${resolveUnref(organizationId)}/projects`, type)
}

export function useBaseProjects(url: MaybeComputedRef<string>, type?: MaybeComputedRef<number>): (pagination: Pagination) => UseFetchReturn<Page<Project>> {
    return (pagination) => {
        const params = new URLSearchParams({
            page: (pagination.pagination.page - 1).toString(),
            size: pagination.pagination.rowsPerPage == 0 ? pagination.pagination.rowsNumber : pagination.pagination.rowsPerPage,
        })
        if (pagination.pagination.sortBy && pagination.pagination.sortBy != 'asc' && pagination.pagination.sortBy != 'desc') {
            params.set("sort", `${pagination.pagination.sortBy},${pagination.pagination.descending ? "desc" : "asc"}`)
        }
        if (type != null) {
            params.set("typeId", String(resolveUnref(type)))
        }
        if (pagination.filter) {
            params.set("search", String(pagination.filter))
        }
        return useFetch<Page<Project>>(`/${resolveUnref(url)}?${params.toString()}`, {initialData: []})
            .get()
            .json()
    }
}

export function useSelectProjects(): UseFetchReturn<Project[]> {
    return useFetch<Project[]>(`/projects/select`, {initialData: []})
        .get()
        .json()
}

export function useProject(id: number): UseFetchReturn<ProjectInfo> {
    return useFetch<Page<Project>>(`/projects/${id}`)
        .get()
        .json()
}

export function useProjectProperties(id: number): UseFetchReturn<ProjectProperty[]> {
    return useFetch<ProjectProperty[]>(`/projects/${id}/properties`)
        .get()
        .json()
}

export function useUpdateProjectProperties(id: number, properties: MaybeComputedRef<ProjectProperty[]>): UseFetchReturn<null> {
    let propertiesBody = computed(() => ({properties: resolveUnref(properties)}));
    return useFetch<null>(`/projects/${id}/properties`, {immediate: false})
        .post(propertiesBody, 'json')
}

export function useProjectPhotoCaption(id: number): UseFetchReturn<ProjectPhotoCaption> {
    return useFetch<ProjectPhotoCaption>(`/projects/${id}/photoCaption`)
        .get()
        .json()
}

export function useUpdateProjectPhotoCaption(id: number, caption: MaybeComputedRef<string | null>): UseFetchReturn<null> {
    let captionBody = computed(() => ({caption: resolveUnref(caption)}));
    return useFetch<null>(`/projects/${id}/photoCaption`, {immediate: false})
        .post(captionBody, 'json')
}

export function useDeleteProject(id: MaybeComputedRef<number>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}`)
    return useFetch<null>(url, {immediate: false})
        .delete()
}


export function useUpdateProjectAvatar(id: MaybeComputedRef<number>, form: FormData): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}/avatar`)
    return useFetch<null>(url)
        .post(form)
}

export function useProjectUsers(id: MaybeComputedRef<number | null>, immediate: boolean = true): UseFetchReturn<ProjectPermission[]> {
    const url = computed(() => !!resolveUnref(id) ? `/projects/${resolveUnref(id)}/permissions` : '')
    return useFetch<ProjectPermission[]>(url, {refetch: () => url.value.length > 0, immediate})
        .get()
        .json()
}


export function useUsers(immediate: boolean = true): UseFetchReturn<ProjectUser[]> {
    return useFetch<ProjectPermission[]>('/permissions', {immediate})
        .get()
        .json()
}


export function useProjectTemplates(id: MaybeComputedRef<number>): UseFetchReturn<ProjectTemplate[]> {
    const url = computed(() => `/projects/${resolveUnref(id)}/templates`)
    return useFetch<ProjectTemplate[]>(url)
        .get()
        .json()
}

export function useProjectTemplate(id: MaybeComputedRef<number>, address: MaybeComputedRef<string>): UseFetchReturn<ArrayBuffer> {
    const url = computed(() => `/projects/${resolveUnref(id)}/templates/${resolveUnref(address)}`)
    return useFetch(url)
        .get()
        .arrayBuffer()
}

export function useCreateProjectTemplate(id: MaybeComputedRef<number>, form: FormData): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}/templates`)
    return useFetch<null>(url)
        .post(form)
}


export function useDeleteProjectTemplate(id: MaybeComputedRef<number>, address: MaybeComputedRef<string>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}/templates/${resolveUnref(address)}`)
    return useFetch<null>(url)
        .delete()
}

export function useUpdateProjectUsers(id: MaybeComputedRef<number>, request: UpdateProjectPermission): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}/permissions`)
    return useFetch<null>(url)
        .post(request)
}

export function useAddProject(request: NewProject): UseFetchReturn<null> {
    return useFetch<null>(`/projects`, {immediate: false})
        .post(request)
}

export function useUpdateProject(id: MaybeComputedRef<number>, request: UpdateProject): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}`)
    return useFetch<null>(url, {immediate: false})
        .put(request)
}

export function useProjectLabels(id: MaybeComputedRef<number | null>, immediate: boolean = true): UseFetchReturn<Label[]> {
    const url = computed(() => !!resolveUnref(id) ? `/projects/${resolveUnref(id)}/labels` : '')
    return useFetch<Label[]>(url, {initialData: [], refetch: () => url.value.length > 0, immediate})
        .get()
        .json()
}

export function useAddProjectLabel(projectId: MaybeComputedRef<number>, newLabel: NewLabel): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/labels`)
    return useFetch<null>(url, {immediate: false})
        .post(newLabel)
}


export function useDeleteProjectLabel(projectId: MaybeComputedRef<number>, id: MaybeComputedRef<number>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/labels/${resolveUnref(id)}`)
    return useFetch<null>(url)
        .delete()
}